// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect, useCallback } from "react";
import { Plus, SquarePen, Trash2, Loader2 } from "lucide-react";
import GlobalAdminHeader from "../../components/admin/GlobalAdminHeader";
import SortableHeader from "../../components/admin/SortableHeader";
import PageLoader from "../../components/PageLoader";
import locationService from "../../services/locationService";
import { formatErrorMessage } from "../../utils/apiHelper";
import useSortableData from "../../hooks/useSortableData";
import { useRoutes } from "../../hooks/useRoutes";
import { hasSuperAdminAccess } from "../../config/roleConfig";
import {
  operatingHoursToState,
  operatingHoursFromState,
} from "../../components/admin/OperatingHoursEditor";
import LocationFormFields from "../../components/admin/LocationFormFields";
import {
  DEFAULT_TIMEZONE,
  DEFAULT_COUNTRY,
} from "../../utils/locationFormHelpers";

const LOCATION_SORT_ACCESSORS = {
  address: (l) =>
    [l.address_postal_code, l.address_street, l.country]
      .filter(Boolean)
      .join(" "),
  spaces: (l) => l.spaces_count ?? l.flaechen_count ?? null,
  status: (l) => (l.is_active ? 1 : 0),
};

const LocationsManagement = () => {
  // Only super admins can create/delete/archive locations.
  const { userRoles } = useRoutes();
  const isSuperAdmin = hasSuperAdminAccess(userRoles || []);

  const getInitialFormData = () => ({
    name: "",
    vhp_code: "",
    slug: "",
    description: "",
    description_html: "",
    latitude: "",
    longitude: "",
    timezone: DEFAULT_TIMEZONE,
    country: DEFAULT_COUNTRY,
    address_street: "",
    address_city: "",
    address_postal_code: "",
    operating_hours: operatingHoursToState(null),
    is_active: true,
    is_public: true,
  });

  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState(null);

  const [formData, setFormData] = useState(getInitialFormData());
  // `slugDirty` is true once the admin has manually edited the slug — we
  // then stop auto-syncing it from the name.
  const [slugDirty, setSlugDirty] = useState(false);

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, overview] = await Promise.all([
        locationService.getLocations({
          page: 1,
          limit: 100,
          sort_by: "name",
          sort_order: "asc",
        }),
        locationService
          .getLocationsOverview({ page: 1, limit: 500 })
          .catch(() => null),
      ]);
      const items = data?.items ?? data?.data?.items ?? [];
      const overviewItems = Array.isArray(overview?.items)
        ? overview.items
        : Array.isArray(overview?.data?.items)
          ? overview.data.items
          : [];
      const countByLocation = overviewItems.reduce((acc, l) => {
        if (l?.id != null) acc[l.id] = l.spaces_count ?? 0;
        return acc;
      }, {});
      const merged = (Array.isArray(items) ? items : []).map((loc) => ({
        ...loc,
        spaces_count: loc.spaces_count ?? countByLocation[loc.id] ?? 0,
      }));
      setLocations(merged);
    } catch (err) {
      console.error("Error loading locations:", err);
      setError(
        err?.response?.data?.detail ??
          "Standorte konnten nicht geladen werden.",
      );
      setLocations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const {
    sortedItems: sortedLocations,
    sortConfig,
    requestSort,
  } = useSortableData(locations, LOCATION_SORT_ACCESSORS);

  const openCreate = () => {
    setActionError(null);
    setFormData(getInitialFormData());
    setSlugDirty(false);
    setEditingLocation(null);
    setShowCreateModal(true);
  };

  const openEdit = (loc) => {
    setActionError(null);
    setEditingLocation(loc);
    // Existing slug → treat as user-set so a name edit doesn't overwrite it.
    setSlugDirty(Boolean(loc.slug?.trim()));
    setFormData({
      name: loc.name ?? "",
      vhp_code: loc.vhp_code?.trim() ?? "",
      slug: loc.slug ?? "",
      description: loc.description ?? "",
      description_html: loc.description_html ?? "",
      latitude: loc.latitude != null ? String(loc.latitude) : "",
      longitude: loc.longitude != null ? String(loc.longitude) : "",
      // Fall back to the dropdown defaults so the select's visible value
      // actually matches formData — otherwise submit would serialise an
      // empty string back to null while the UI shows "Europe/Berlin" / "DEU".
      timezone: loc.timezone || DEFAULT_TIMEZONE,
      country: loc.country || DEFAULT_COUNTRY,
      address_street: loc.address_street ?? "",
      address_city: loc.address_city ?? "",
      address_postal_code: loc.address_postal_code ?? "",
      operating_hours: operatingHoursToState(loc.operating_hours),
      is_active: loc.is_active ?? true,
      is_public: loc.is_public ?? true,
    });
  };

  const closeEdit = () => {
    setActionError(null);
    setEditingLocation(null);
    setFormData(getInitialFormData());
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim()) return;
    setSaving(true);
    setActionError(null);
    try {
      await locationService.createLocation({
        name: formData.name.trim(),
        vhp_code: formData.vhp_code?.trim() || null,
        slug: formData.slug?.trim() || null,
        description: formData.description?.trim() || null,
        description_html: formData.description_html?.trim() || null,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        timezone: formData.timezone?.trim() || null,
        country: formData.country?.trim() || null,
        address_street: formData.address_street?.trim() || null,
        address_city: formData.address_city?.trim() || null,
        address_postal_code: formData.address_postal_code?.trim() || null,
        operating_hours: operatingHoursFromState(formData.operating_hours),
        is_active: formData.is_active,
        is_public: formData.is_public,
      });
      await fetchLocations();
      setShowCreateModal(false);
      closeEdit();
    } catch (err) {
      setActionError(formatErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim() || !editingLocation) return;
    setSaving(true);
    setActionError(null);
    try {
      await locationService.updateLocation(editingLocation.id, {
        name: formData.name.trim(),
        vhp_code: formData.vhp_code?.trim() || null,
        slug: formData.slug?.trim() || null,
        description: formData.description?.trim() || null,
        description_html: formData.description_html?.trim() || null,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        timezone: formData.timezone?.trim() || null,
        country: formData.country?.trim() || null,
        address_street: formData.address_street?.trim() || null,
        address_city: formData.address_city?.trim() || null,
        address_postal_code: formData.address_postal_code?.trim() || null,
        operating_hours: operatingHoursFromState(formData.operating_hours),
        is_active: formData.is_active,
        is_public: formData.is_public,
      });
      await fetchLocations();
      closeEdit();
    } catch (err) {
      setActionError(formatErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (loc) => {
    setActionError(null);
    setDeleteConfirm(loc);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    setSaving(true);
    setActionError(null);
    try {
      await locationService.deleteLocation(deleteConfirm.id);
      await fetchLocations();
      setDeleteConfirm(null);
    } catch (err) {
      setActionError(formatErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative">
      <GlobalAdminHeader
        title="Standorte"
        subtitle="Standorte verwalten"
        buttons={
          isSuperAdmin
            ? [
                {
                  text: "Neuer Standort",
                  icon: <Plus className="h-4 w-4" />,
                  onClick: openCreate,
                  className:
                    "flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-color text-white font-medium shadow-lg hover:opacity-90 transition-opacity",
                },
              ]
            : []
        }
      />

      <div className="mx-auto px-4 lg:px-4 py-4 text-left dark:text-slate-200">
        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50">
                  <SortableHeader
                    label="Name"
                    sortKey="name"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    label="Stadt"
                    sortKey="address_city"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    label="Adresse"
                    sortKey="address"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    label="Spaces"
                    sortKey="spaces"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <SortableHeader
                    label="Status"
                    sortKey="status"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <th className="text-right text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider px-4 py-3">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                {loading ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-8 text-center text-gray-500 dark:text-slate-400 text-sm"
                    >
                      <PageLoader message="Lade Standorte..." />
                    </td>
                  </tr>
                ) : locations.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-8 text-center text-gray-500 dark:text-slate-400 text-sm"
                    >
                      Keine Standorte gefunden.
                    </td>
                  </tr>
                ) : (
                  sortedLocations.map((loc) => (
                    <tr
                      key={loc.id}
                      className="hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors"
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-slate-100">
                        {loc.name}
                      </td>
                      <td
                        className="px-4 py-3 text-sm text-gray-600 dark:text-slate-300 max-w-xs truncate"
                        title={loc.address_city || undefined}
                      >
                        {loc.address_city?.trim() || "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-300">
                        {loc.address_street ||
                        loc.country ||
                        loc.address_postal_code
                          ? [
                              loc.address_postal_code,
                              loc.address_street,
                              loc.country,
                            ]
                              .filter(Boolean)
                              .join(" ")
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-300 font-mono">
                        {loc.spaces_count ?? loc.flaechen_count ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-slate-300">
                        <div className="flex flex-col gap-1">
                          <span>{loc.is_active ? "Aktiv" : "Inaktiv"}</span>
                          {/* <span>{loc.is_public ? "Öffentlich" : "Privat"}</span> */}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => openEdit(loc)}
                            className="p-2 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 transition-colors"
                            title="Bearbeiten"
                          >
                            <SquarePen className="w-5 h-5" />
                          </button>
                          {isSuperAdmin && (
                            <button
                              type="button"
                              onClick={() => handleDeleteClick(loc)}
                              className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-500 dark:text-red-400 transition-colors"
                              title="Löschen"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create – centered modal (Mandanten style) */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => !saving && setShowCreateModal(false)}
            aria-hidden
          />
          <div
            className="relative w-full max-w-5xl h-[94vh] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col dark:border dark:border-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700 shrink-0">
              <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100">
                Neuer Standort
              </h2>
            </div>
            <form
              onSubmit={handleCreateSubmit}
              className="flex-1 overflow-y-auto p-6 lg:p-8 space-y-5"
            >
              {actionError && showCreateModal && (
                <div className="px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
                  {actionError}
                </div>
              )}
              <LocationFormFields
                formData={formData}
                setFormData={setFormData}
                slugDirty={slugDirty}
                setSlugDirty={setSlugDirty}
              />
              <div className="sticky bottom-0 bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm border-t border-gray-200 dark:border-slate-700 -mx-6 lg:-mx-8 px-6 lg:px-8 pt-4 pb-2 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  disabled={saving}
                  className="px-4 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2.5 rounded-xl bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Anlegen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit – centered modal (Mandanteneinstellungen style) */}
      {editingLocation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => !saving && closeEdit()}
            aria-hidden
          />
          <div
            className="relative w-full max-w-5xl h-[94vh] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col dark:border dark:border-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative px-6 py-4 border-b border-gray-100 dark:border-slate-700 shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/5 via-cyan-500/5 to-emerald-500/5 dark:from-indigo-500/10 dark:via-cyan-500/10 dark:to-emerald-500/10" />
              <div className="relative">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100">
                    Standort bearbeiten
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                    {editingLocation.name}
                  </p>
                </div>
              </div>
            </div>
            <form
              onSubmit={handleEditSubmit}
              className="flex-1 overflow-y-auto p-6 lg:p-8 space-y-5"
            >
              {actionError && editingLocation && (
                <div className="px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
                  {actionError}
                </div>
              )}
              <LocationFormFields
                formData={formData}
                setFormData={setFormData}
                slugDirty={slugDirty}
                setSlugDirty={setSlugDirty}
              />
              <div className="sticky bottom-0 bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm border-t border-gray-200 dark:border-slate-700 -mx-6 lg:-mx-8 px-6 lg:px-8 pt-4 pb-2 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeEdit}
                  disabled={saving}
                  className="px-4 py-2.5 rounded-xl border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2.5 rounded-xl bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Speichern
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation – centered popup (Mandanten style) */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => !saving && setDeleteConfirm(null)}
            aria-hidden
          />
          <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl dark:border dark:border-slate-700 max-w-md w-full p-6">
            {actionError && deleteConfirm && (
              <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
                {actionError}
              </div>
            )}
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-1">
                  Standort löschen?
                </h3>
                <p className="text-sm text-gray-600 dark:text-slate-400 mb-1">
                  „{deleteConfirm.name ?? "Unbekannt"}“ wird dauerhaft gelöscht.
                  Diese Aktion kann nicht rückgängig gemacht werden.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                disabled={saving}
                className="px-5 py-2.5 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 font-medium transition-colors disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={saving}
                className="px-5 py-2.5 rounded-xl bg-red-600 dark:bg-red-500 text-white font-medium hover:bg-red-700 dark:hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Trash2 className="w-5 h-5" />
                )}
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationsManagement;
